import React, { lazy, useEffect, useMemo, useRef, useState } from "react";
import Header from "./Header/Header";
import { lazyWithPreload } from "react-lazy-with-preload";
import LazyComponent from "components/Lazy/LazyLoad";
import { Floater } from "@commerce-core/react-components";

const Hero = lazyWithPreload(() => import("components/Hero/Hero"));
const Zigzag = lazy(() => import("components/Zigzag/Zigzag"));
const BeforeAfter = lazy(() => import("components/BeforeAfter/BeforeAfter"));
const DidYouKnow = lazy(() => import("components/DidYouKnow/DidYouKnow"));
const BenefitsSection = lazy(() => import("components/BenefitsSection/BenefitsSection"));
const ReviewSection = lazy(() => import("components/ReviewSection/ReviewSection"));
const HappyCustomersSection = lazy(() => import("components/HappyCustomersSection/HappyCustomersSection"));
const DropdownSection = lazy(() => import("components/DropdownSection/DropdownSection"));
const CustomerReviews = lazy(() => import("components/CustomerReviews/CustomerReviews"));
const Footer = lazy(() => import("../layouts/Footer/Footer"));

interface CustomWindow extends Window {
  hybridLander?: any;
}
declare let window: CustomWindow;

const Layout: React.FC = () => {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [showFloater, setShowFloater] = useState(false);
  const useFaviconInHeader =
    window?.hybridLander?.hybrid_lander_v1_page_settings?.hybrid_lander_v1_use_favicon_in_header;
  const useGlobalFavicon = window?.hybridLander?.hybrid_lander_v1_page_settings?.hybrid_lander_v1_global_favicon;
  const favicon = window?.hybridLander?.hybrid_lander_v1_page_settings?.hybrid_lander_v1_favicon ?? "";
  const floaterButtonColor = window?.hybridLander?.hybrid_lander_v1_floater?.button_color;
  const floaterButtonTextColor = window?.hybridLander?.hybrid_lander_v1_floater?.button_text_color;
  const enableFloater = window?.hybridLander?.hybrid_lander_v1_floater?.enable_floater;

  useEffect(() => {
    Hero.preload();
  }, []);

  const templateStyles = useMemo((): React.CSSProperties => {
    const colors = window?.hybridLander?.hybrid_lander_v1_colors;
    const floater = window?.hybridLander?.hybrid_lander_v1_floater;
    const primaryColor = colors?.hybrid_lander_v1_colors_primary;
    const secondaryColor = colors?.hybrid_lander_v1_colors_secondary;
    const dividersColor = colors?.hybrid_lander_v1_colors_dividers;
    const lightSectionColor = colors?.hybrid_lander_v1_colors_lightSectionBg;
    const darkSectionColor = colors?.hybrid_lander_v1_colors_darkSectionBg;
    const dotsColor = colors?.hybrid_lander_v1_colors_dots;
    const firstGradient = colors?.hybrid_lander_v1_colors_gradientOne;
    const secondGradient = colors?.hybrid_lander_v1_colors_gradientTwo;
    const ctaBg = colors?.hybrid_lander_v1_colors_ctaBg;
    const ctaHover = colors?.hybrid_lander_v1_colors_ctaHover;
    const ctaBottom = colors?.hybrid_lander_v1_colors_ctaBottomBorder;
    const textColor = colors?.hybrid_lander_v1_colors_textMainColor;
    const floaterBgColor = floater?.background_gradient;
    const floaterTextColor = floater?.text_color;

    // additional colors
    const useAditionalColors = colors?.hybrid_lander_v1_colors_use_aditional_colors;
    const secondaryHeadingColor = colors?.hybrid_lander_v1_benefits_discountSecondaryHeading;
    const secondaryBackgroundColor = colors?.hybrid_lander_v1_colors_secondary_background;
    const primaryBackgroundColor = colors?.hybrid_lander_v1_colors_primary_background;
    const heroAwardBackgroundColor = colors?.hybrid_lander_v1_hero_award_background_color;
    const featuresPrimaryColor = colors?.hybrid_lander_v1_happycustomers_features_primary_text_color;
    const happyCustomerSecondaryColor = colors?.hybrid_lander_v1_happycustomers_secondary_heading_color;
    const buttonTextColor = colors?.hybrid_lander_v1_colors_cta_text_color;
    const featureListBackgroundColor = colors?.hybrid_lander_v1_features_list_background_color;

    return {
      ["--primary-color" as any]: primaryColor,
      ["--secondary-color" as any]: secondaryColor,
      ["--dividers-color" as any]: dividersColor,
      ["--light-section-color" as any]: lightSectionColor,
      ["--dark-section-color" as any]: darkSectionColor,
      ["--dots-color" as any]: dotsColor,
      ["--first-gradient" as any]: firstGradient,
      ["--second-gradient" as any]: secondGradient,
      ["--cta-bg" as any]: ctaBg,
      ["--cta-hover" as any]: ctaHover,
      ["--cta-bottom" as any]: ctaBottom,
      ["--text-color" as any]: textColor,
      ["--floater-bg-color" as any]: floaterBgColor,
      ["--floater-text-color" as any]: floaterTextColor,

      // additional
      ["--secondary-heading-color" as any]: useAditionalColors ? secondaryHeadingColor : secondaryColor,
      ["--secondary-background-color" as any]: useAditionalColors ? secondaryBackgroundColor : secondaryColor,
      ["--primary-background-color" as any]: useAditionalColors ? primaryBackgroundColor : primaryColor,
      ["--hero-award-backGround" as any]: useAditionalColors ? heroAwardBackgroundColor : darkSectionColor,
      ["--happy-customer-features-primaryText" as any]: useAditionalColors ? featuresPrimaryColor : secondaryColor,
      ["--happy-customer-secondaryHeading" as any]: useAditionalColors ? happyCustomerSecondaryColor : secondaryColor,
      ["--button-text-color" as any]: useAditionalColors ? buttonTextColor : textColor,
      ["--feature-background" as any]: useAditionalColors ? featureListBackgroundColor : secondaryColor,
    };
  }, []);

  useEffect(() => {
    if (!enableFloater) {
      setShowFloater(false);
      return;
    }

    const el = heroRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(([entry]) => {
      setShowFloater(!entry.isIntersecting);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [enableFloater]);

  return (
    <div style={templateStyles}>
      <Header useFavicon={useFaviconInHeader} useGlobalFavicon={useGlobalFavicon} favicon={favicon} />
      <main>
        <Hero ref={heroRef} />
        <LazyComponent Component={Zigzag} />
        <LazyComponent Component={BeforeAfter} />
        <LazyComponent Component={DidYouKnow} />
        <LazyComponent Component={BenefitsSection} />
        <LazyComponent Component={ReviewSection} />
        <LazyComponent Component={HappyCustomersSection} />
        <LazyComponent Component={DropdownSection} />
        <LazyComponent Component={CustomerReviews} />
      </main>
      <LazyComponent Component={Footer} />

      {enableFloater && showFloater && (
        <Floater
          floater={window?.hybridLander?.hybrid_lander_v1_floater}
          classes={{ floaterInfo: "no-gap", labelText: "block md:block lg:block lg:text-base font-16" }}
        >
          <a
            className="w-fit text-lg px-6 py-3 rounded-[10px] font-bold floater-button text-center"
            style={{
              backgroundColor: floaterButtonColor,
              color: floaterButtonTextColor,
            }}
            href={window?.cta_link || "#"}
            target="_blank"
            rel="noopener noreferrer"
          >
            {window?.hybridLander?.hybrid_lander_v1_floater?.tr_button_text}
          </a>
        </Floater>
      )}
    </div>
  );
};

export default Layout;
